import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  Divider,
  Paper,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { ordersService } from '../../services/ordersService';
import productsService from '../../services/productsService';
import { useNotification } from '../../contexts/NotificationContext';

/**
 * Composant modal pour créer une commande manuelle (admin - vente directe)
 */
const CreateManualOrderModal = ({ open, onClose, onSuccess }) => {
  const { showNotification } = useNotification();
  
  // États pour les informations client
  const [customerInfo, setCustomerInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    country: 'France',
  });

  // États pour les produits
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [cartItems, setCartItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  // États pour la commande
  const [shippingCost, setShippingCost] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [status, setStatus] = useState('confirmed');
  const [notes, setNotes] = useState('');

  // États UI
  const [loading, setLoading] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [error, setError] = useState(null);

  const normalizeProductList = useMemo(() => async (rawProducts = []) => {
    if (!Array.isArray(rawProducts)) {
      return [];
    }

    return Promise.all(
      rawProducts.map(async (product) => {
        // Si les variantes sont déjà présentes, les utiliser directement
        const existingVariants = product.variants || product.product_variants;
        if (Array.isArray(existingVariants) && existingVariants.length > 0) {
          return {
            ...product,
            variants: existingVariants,
          };
        }

        // Sinon, récupérer le détail du produit
        try {
          const detailResponse = await productsService.getProduct(product.id);
          const detailData = detailResponse.data?.product || detailResponse.data || detailResponse;
          const variants = detailData?.product_variants || detailData?.variants || [];
          return {
            ...product,
            variants,
          };
        } catch (detailError) {
          console.error(`Erreur lors du chargement des variantes pour le produit ${product.id}:`, detailError);
          return {
            ...product,
            variants: [],
          };
        }
      })
    );
  }, []);

  const fetchProducts = async (fetcher, showEmptyMessage = true) => {
    try {
      setLoadingProducts(true);
      const response = await fetcher();
      const body = response?.data || response || {};
      const list = body.products || body.items || body.data || body;
      const normalized = await normalizeProductList(list);
      setProducts(normalized);

      if (showEmptyMessage && normalized.length === 0) {
        showNotification('Aucun produit trouvé', 'info');
      }
    } catch (fetchError) {
      console.error('Erreur lors du chargement des produits:', fetchError);
      showNotification('Erreur lors du chargement des produits', 'error');
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  const loadProducts = async () => {
    await fetchProducts(() => productsService.getAllProducts(), false);
  };

  const searchProducts = async (query) => {
    if (!query || query.trim().length === 0) {
      await loadProducts();
      return;
    }
    await fetchProducts(() => productsService.searchProducts(query.trim()));
  };

  useEffect(() => {
    if (open) {
      resetForm();
      setSearchQuery('');
      loadProducts();
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handler = setTimeout(() => {
      if (searchQuery && searchQuery.trim().length > 0) {
        searchProducts(searchQuery);
      } else {
        loadProducts();
      }
    }, 350);

    return () => clearTimeout(handler);
  }, [searchQuery, open]);

  const resetForm = () => {
    setCustomerInfo({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      postalCode: '',
      country: 'France',
    });
    setCartItems([]);
    setShippingCost(0);
    setPaymentMethod('card');
    setStatus('confirmed');
    setNotes('');
    setSelectedProduct(null);
    setSelectedVariant(null);
    setQuantity(1);
    setError(null);
  };

  const createItemKey = (productId, variantId) => variantId ? `variant-${variantId}` : `product-${productId}`;

  const handleAddToCart = () => {
    if (!selectedProduct) {
      showNotification('Veuillez sélectionner un produit', 'warning');
      return;
    }

    const hasVariants = availableVariants.length > 0;
    if (hasVariants && !selectedVariant) {
      showNotification('Veuillez sélectionner une variante', 'warning');
      return;
    }

    const variantToUse = hasVariants ? selectedVariant : null;
    const rawPriceHt = variantToUse ? Number(variantToUse.priceHt ?? 0) : Number(selectedProduct.priceHt ?? 0);
    const rawPriceTtc = variantToUse ? Number(variantToUse.priceTtc ?? 0) : Number(selectedProduct.priceTtc ?? 0);
    const derivedPriceTtc = rawPriceTtc || (rawPriceHt ? rawPriceHt * 1.2 : 0);
    const derivedPriceHt = rawPriceHt || (rawPriceTtc ? rawPriceTtc / 1.2 : 0);
    const roundRate = (value) => Math.round((value ?? 0) * 100) / 100;
    const rawTvaRate = variantToUse?.tva?.taux ?? selectedProduct?.tva?.taux ?? (derivedPriceHt > 0 ? ((derivedPriceTtc - derivedPriceHt) / derivedPriceHt) * 100 : 20);
    const tvaRate = roundRate(rawTvaRate);

    const key = createItemKey(selectedProduct.id, variantToUse?.id);
    const existingItem = cartItems.find(item => item.key === key);

    const newItem = {
      key,
      productId: selectedProduct.id,
      productVariantId: variantToUse ? variantToUse.id : null,
      quantity,
      productName: selectedProduct.name,
      variantName: variantToUse?.name || null,
      priceHt: derivedPriceHt,
      priceTtc: derivedPriceTtc,
      tvaRate,
    };

    if (existingItem) {
      setCartItems(cartItems.map(item =>
        item.key === key
          ? { ...item, quantity: item.quantity + quantity }
          : item
      ));
    } else {
      setCartItems([...cartItems, newItem]);
    }

    // Réinitialiser la sélection
    setSelectedProduct(null);
    setSelectedVariant(null);
    setQuantity(1);
  };

  const handleRemoveFromCart = (itemKey) => {
    setCartItems(cartItems.filter(item => item.key !== itemKey));
  };

  const handleUpdateQuantity = (itemKey, newQuantity) => {
    if (newQuantity <= 0) {
      handleRemoveFromCart(itemKey);
      return;
    }
    setCartItems(cartItems.map(item =>
      item.key === itemKey
        ? { ...item, quantity: newQuantity }
        : item
    ));
  };

  const handleUpdatePrice = (itemKey, priceType, rawValue) => {
    const sanitizedValue =
      typeof rawValue === 'string' ? rawValue.replace(',', '.') : rawValue;
    const parsedValue = parseFloat(sanitizedValue);

    if (Number.isNaN(parsedValue) || parsedValue < 0) {
      return;
    }

    setCartItems(cartItems.map(item => {
      if (item.key !== itemKey) {
        return item;
      }

      const vatRate = item.tvaRate ?? 0;
      const vatMultiplier = 1 + vatRate / 100;

      if (priceType === 'ht') {
        const priceHt = parsedValue;
        const priceTtc = vatMultiplier ? priceHt * vatMultiplier : priceHt;
        return {
          ...item,
          priceHt,
          priceTtc,
        };
      }

      const priceTtc = parsedValue;
      const priceHt = vatMultiplier ? priceTtc / vatMultiplier : priceTtc;
      return {
        ...item,
        priceHt,
        priceTtc,
      };
    }));
  };

  const calculateTotal = () => {
    const subtotalHt = cartItems.reduce((sum, item) => sum + (Number(item.priceHt) || 0) * item.quantity, 0);
    const taxBreakdown = cartItems.reduce((acc, item) => {
      const normalizedRate = Math.round(((item.tvaRate ?? 20)) * 100) / 100;
      const key = normalizedRate.toFixed(2);
      const amountHt = (item.priceHt || 0) * item.quantity;
      const amountTva = amountHt * (normalizedRate / 100);
      acc[key] = (acc[key] || 0) + amountTva;
      return acc;
    }, {});
    const totalTva = Object.values(taxBreakdown).reduce((sum, val) => sum + val, 0);
    const shippingTtc = Number(shippingCost || 0);
    const totalTtc = subtotalHt + totalTva + shippingTtc;

    return {
      subtotalHt,
      taxBreakdown,
      totalTva,
      shippingTtc,
      totalTtc,
    };
  };

  const handleSubmit = async () => {
    // Validation
    if (!customerInfo.firstName || !customerInfo.lastName) {
      setError('Le prénom et le nom du client sont requis');
      return;
    }

    if (cartItems.length === 0) {
      setError('Veuillez ajouter au moins un produit à la commande');
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const orderData = {
        customerInfo,
        items: cartItems.map(item => ({
          productVariantId: item.productVariantId || undefined,
          productId: item.productVariantId ? undefined : item.productId,
          quantity: item.quantity,
          unitPriceHt: item.priceHt !== undefined ? Number(item.priceHt) : undefined,
          unitPriceTtc: item.priceTtc !== undefined ? Number(item.priceTtc) : undefined,
          tvaRate: item.tvaRate !== undefined ? Number(item.tvaRate) : undefined,
        })),
        deliveryAddress: {
          firstName: customerInfo.firstName,
          lastName: customerInfo.lastName,
          address: customerInfo.address,
          city: customerInfo.city,
          postalCode: customerInfo.postalCode,
          country: customerInfo.country,
          phone: customerInfo.phone,
        },
        billingAddress: {
          firstName: customerInfo.firstName,
          lastName: customerInfo.lastName,
          address: customerInfo.address,
          city: customerInfo.city,
          postalCode: customerInfo.postalCode,
          country: customerInfo.country,
        },
        shippingCost,
        paymentMethod,
        status,
        notes,
      };

      const response = await ordersService.createManualOrder(orderData);
      showNotification('Commande créée avec succès', 'success');
      onSuccess && onSuccess(response.data.order);
      onClose();
    } catch (error) {
      console.error('Erreur lors de la création de la commande:', error);
      setError(error.response?.data?.message || 'Erreur lors de la création de la commande');
      showNotification('Erreur lors de la création de la commande', 'error');
    } finally {
      setLoading(false);
    }
  };

  const totals = calculateTotal();
  // Les variantes peuvent être dans product_variants ou variants selon la structure
  const productVariants = selectedProduct?.variants || selectedProduct?.product_variants || [];
  const availableVariants = productVariants.filter(v => v.isActive !== false && (v.stock > 0 || v.stock === undefined));
  const canAddToCart = quantity > 0 && (availableVariants.length === 0 || selectedVariant);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Créer une commande manuelle</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Informations client */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Informations client
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Prénom *"
                  value={customerInfo.firstName}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, firstName: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nom *"
                  value={customerInfo.lastName}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, lastName: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={customerInfo.email}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Téléphone"
                  value={customerInfo.phone}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Adresse"
                  value={customerInfo.address}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, address: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Code postal"
                  value={customerInfo.postalCode}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, postalCode: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Ville"
                  value={customerInfo.city}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, city: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Pays"
                  value={customerInfo.country}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, country: e.target.value })}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Ajout de produits */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Ajouter des produits
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Rechercher un produit"
                  placeholder="Nom, SKU, référence..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth disabled={loadingProducts}>
                  <InputLabel>Produit</InputLabel>
                  <Select
                    value={selectedProduct?.id || ''}
                    onChange={(e) => {
                      const product = products.find(p => p.id === parseInt(e.target.value, 10));
                      setSelectedProduct(product || null);
                      setSelectedVariant(null);
                    }}
                  >
                    {products.map((product) => (
                      <MenuItem key={product.id} value={product.id}>
                        {product.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {selectedProduct && availableVariants.length > 0 && (
                <Grid item xs={12} sm={2}>
                  <FormControl fullWidth disabled={loadingProducts}>
                    <InputLabel>Variante</InputLabel>
                    <Select
                      value={selectedVariant?.id || ''}
                      onChange={(e) => {
                        const variant = availableVariants.find(v => v.id === parseInt(e.target.value, 10));
                        setSelectedVariant(variant || null);
                      }}
                    >
                      {availableVariants.map((variant) => {
                        const priceValue = Number(variant.priceTtc ?? variant.price ?? 0) || 0;
                        const stockValue = variant.stock !== undefined ? variant.stock : undefined;
                        return (
                          <MenuItem key={variant.id} value={variant.id}>
                            {variant.name} - {priceValue.toFixed(2)}€
                            {stockValue !== undefined ? ` (Stock: ${stockValue})` : ''}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              {selectedProduct && availableVariants.length === 0 && (
                <Grid item xs={12} sm={2}>
                  <Box
                    sx={{
                      p: 1,
                      textAlign: 'center',
                      borderRadius: 1,
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Aucune variante pour ce produit
                    </Typography>
                  </Box>
                </Grid>
              )}
              <Grid item xs={12} sm={1.5}>
                <TextField
                  fullWidth
                  type="number"
                  label="Quantité"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value, 10) || 1))}
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={1.5}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddToCart}
                  disabled={!canAddToCart}
                >
                  Ajouter
                </Button>
              </Grid>
            </Grid>
            {loadingProducts && (
              <Box display="flex" alignItems="center" gap={1} mt={2}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Chargement des produits...
                </Typography>
              </Box>
            )}
          </Grid>

          {/* Panier */}
          {cartItems.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Panier ({cartItems.length} {cartItems.length > 1 ? 'articles' : 'article'})
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Produit</TableCell>
                      <TableCell align="right">Prix unitaire</TableCell>
                      <TableCell align="center">Quantité</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cartItems.map((item) => (
                      <TableRow key={item.key}>
                        <TableCell>
                          {item.productName}
                          {item.variantName ? ` - ${item.variantName}` : ''}
                        </TableCell>
                        <TableCell align="right">
                          <Box display="flex" flexDirection="column" gap={1}>
                            <TextField
                              type="number"
                              size="small"
                              label="Prix HT"
                              value={(item.priceHt || 0).toFixed(2)}
                              onChange={(e) => handleUpdatePrice(item.key, 'ht', e.target.value)}
                              inputProps={{ min: 0, step: 0.01 }}
                            />
                            <TextField
                              type="number"
                              size="small"
                              label="Prix TTC"
                              value={(item.priceTtc || 0).toFixed(2)}
                              onChange={(e) => handleUpdatePrice(item.key, 'ttc', e.target.value)}
                              inputProps={{ min: 0, step: 0.01 }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <TextField
                            type="number"
                            size="small"
                            value={item.quantity}
                            onChange={(e) => handleUpdateQuantity(item.key, parseInt(e.target.value, 10) || 1)}
                            inputProps={{ min: 1, style: { textAlign: 'center', width: '60px' } }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">{((item.priceHt || 0) * item.quantity).toFixed(2)}€ HT</Typography>
                          <Typography variant="caption" color="text.secondary">{((item.priceTtc || 0) * item.quantity).toFixed(2)}€ TTC</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveFromCart(item.key)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          )}

          {/* Options de commande */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Options de commande
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Frais de livraison (€)"
                  value={shippingCost}
                  onChange={(e) => setShippingCost(parseFloat(e.target.value) || 0)}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Méthode de paiement</InputLabel>
                  <Select
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  >
                    <MenuItem value="card">Carte bancaire</MenuItem>
                    <MenuItem value="cash">Espèces</MenuItem>
                    <MenuItem value="bank_transfer">Virement bancaire</MenuItem>
                    <MenuItem value="check">Chèque</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Statut</InputLabel>
                  <Select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    <MenuItem value="pending">En attente</MenuItem>
                    <MenuItem value="confirmed">Confirmée</MenuItem>
                    <MenuItem value="processing">En traitement</MenuItem>
                    <MenuItem value="shipped">Expédiée</MenuItem>
                    <MenuItem value="delivered">Livrée</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Notes internes ou instructions spéciales..."
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Récapitulatif */}
          {cartItems.length > 0 && (
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Récapitulatif
                </Typography>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Sous-total HT:</Typography>
                  <Typography>{totals.subtotalHt.toFixed(2)}€</Typography>
                </Box>
                {Object.entries(totals.taxBreakdown).map(([rate, amount]) => (
                  <Box key={rate} display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography>TVA {rate}%:</Typography>
                    <Typography>{amount.toFixed(2)}€</Typography>
                  </Box>
                ))}
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Total TVA:</Typography>
                  <Typography>{totals.totalTva.toFixed(2)}€</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Frais de livraison (TTC):</Typography>
                  <Typography>{totals.shippingTtc.toFixed(2)}€</Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="h6">Total TTC:</Typography>
                  <Typography variant="h6">{totals.totalTtc.toFixed(2)}€</Typography>
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Annuler
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || cartItems.length === 0}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Création...' : 'Créer la commande'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateManualOrderModal;

